import React, { useRef, useState, useEffect } from "react";
import { Tabs, FloatingIndicator, type TabsProps } from "@mantine/core";
import styles from "@scss/custom-components/tabs.module.scss";
import cx from "clsx";

type TabSize = "xs" | "sm" | "md" | "lg" | "xl";

interface CustomUnderlineTabsProps extends TabsProps {
  values: string[];
  tabs: React.ReactNode[];
  panels: React.ReactNode[];
  size?: TabSize;
  childWithDot?: number;
  tabGrow?: boolean;
  centerTabs?: boolean;
}

export function CustomUnderlineTabs({
  tabs,
  values,
  panels,
  size = "md",
  variant = "green",
  childWithDot = -1,
  tabGrow = true,
  centerTabs = false,
  ...props
}: CustomUnderlineTabsProps) {
  const [activeTab, setActiveTab] = useState<string>(values[0]);
  const [mounted, setMounted] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  const setTabRef = (value: string) => (node: HTMLButtonElement | null) => {
    tabRefs.current[value] = node;
  };

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <Tabs
      value={activeTab}
      onChange={(value) => {
        if (value) setActiveTab(value);
      }}
      variant={variant}
      {...props}
      classNames={{
        root: styles.underlineTabs,
        list: `${styles.list} ${styles.underlineList}`,
        tab: styles.tab,
      }}
    >
      <Tabs.List
        ref={rootRef}
        grow={tabGrow}
        className={cx(
          centerTabs && styles.listCenter,
          tabGrow ? styles.listGrow : styles.listCompact,
        )}
      >
        {tabs.map((label, index) => {
          const isDot = childWithDot === index;
          const value = values[index];

          return (
            <Tabs.Tab
              key={value}
              value={value}
              ref={setTabRef(value)}
              className={cx(
                styles.tab,
                size ? styles[`tabSize--${size}`] : "",
                isDot && styles[`dotTab`],
              )}
            >
              {isDot && (
                <></>
              )}
              {label}
            </Tabs.Tab>
          );
        })}

        {mounted && tabRefs.current[activeTab] && (
          <FloatingIndicator
            target={tabRefs.current[activeTab]}
            parent={rootRef.current}
            className={styles.underlineFloatingIndicator}
          />
        )}
      </Tabs.List>

      {values.map((value, index) => (
        <Tabs.Panel key={value} value={value}>
          {panels[index]}
        </Tabs.Panel>
      ))}
    </Tabs>
  );
}
